import { json } from '@codemirror/lang-json'
import { EditorState } from '@codemirror/state'
import { EditorView, lineNumbers } from '@codemirror/view'
import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchConfig } from './api'

interface Props {
  slug: string
}

export function ConfigEditor({ slug }: Props) {
  const [config, setConfig] = useState<Record<string, unknown> | null>(null)
  const [meta, setMeta] = useState<{ updated_at: string, updated_by: string | null } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const hostRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)

  useEffect(() => {
    let cancelled = false
    setConfig(null)
    setError(null)
    fetchConfig(slug)
      .then((res) => {
        if (cancelled) return
        setConfig(res.config)
        setMeta({ updated_at: res.updated_at, updated_by: res.updated_by })
      })
      .catch((err) => {
        if (cancelled) return
        setError(String(err))
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  const pretty = useMemo(
    () => (config ? JSON.stringify(config, null, 2) : ''),
    [config],
  )

  useEffect(() => {
    if (!hostRef.current || !pretty) return
    viewRef.current?.destroy()
    viewRef.current = new EditorView({
      state: EditorState.create({
        doc: pretty,
        extensions: [
          lineNumbers(),
          json(),
          EditorView.editable.of(false),
          EditorState.readOnly.of(true),
        ],
      }),
      parent: hostRef.current,
    })
    return () => {
      viewRef.current?.destroy()
      viewRef.current = null
    }
  }, [pretty])

  if (error) return <div className="ad-msg is-err">{error}</div>
  if (!config) return <div className="ad-msg">Loading {slug}…</div>

  return (
    <div className="ad-editor is-read-only">
      <div className="ad-editor-head">
        <h2 className="b-h2">{slug}</h2>
        <span className="b-mono-dim b-tiny">
          UPDATED
          {' '}
          {meta?.updated_at ? new Date(meta.updated_at).toISOString().slice(0, 16) : '—'}
        </span>
      </div>
      <div className="ad-editor-section">
        <div ref={hostRef} />
      </div>
    </div>
  )
}
